/**
 * Pure reducer for Build tab (Brain domain) state management.
 *
 * Replaces the scattered imperative `setX()` calls in BuildTab's
 * `handleWebSocketEvent` with a single state transition function.
 *
 * Key invariant: `clientMessages` (user prompts) are NEVER mutated by
 * server events. Server events only project into `stages`, `activeStage`,
 * `artifact`, `isProcessing`, `inputState`, and approval-related fields.
 */
import { aslToReactFlow } from './aslToReactFlow.js';
import { manifestToReactFlow } from './manifestToReactFlow.js';

const APPROVAL_STAGE_ID = 'approval';

// Ephemeral "build activity" slice — agent rows (queued→writing→done) + skill
// rows + the current phase, driven by BUILD_* events during network generation.
// NEVER persisted; cleared whenever the build stops (terminal/failed/new prompt).
const EMPTY_BUILD_ACTIVITY = { phase: null, agents: {}, skills: {}, playbook: {}, prompt: null, taskId: null, architectUsage: null, reasoning: '', reasoningDone: false };
// Monotonic phase ranks so an out-of-order / duplicate row event can never
// downgrade a row (e.g. a late "queued" arriving after "done").
const AGENT_PHASE_RANK = { queued: 0, writing: 1, done: 2 };
const SKILL_PHASE_RANK = { queued: 0, drafting: 1, done: 2 };
// Playbook (project networks): one row per milestone — queued → planning → done, mirroring skills.
const PLAYBOOK_PHASE_RANK = { queued: 0, planning: 1, done: 2 };

function graphFromDesign(design) {
  // Tolerate a JSON-string design (the restore endpoint serializes the interface)
  // so every caller — live APPROVAL_REQUESTED, RESTORE_APPROVAL, HYDRATE_GRAPH —
  // can pass either shape without each re-parsing.
  if (typeof design === 'string') {
    try { design = JSON.parse(design); } catch { return null; }
  }
  if (!design || typeof design !== 'object') return null;
  // GENERATIVE (Brain2) design is a MANIFEST tree — render it via manifestToReactFlow
  // (GENERATIVE-NETWORKS.md §visualization). Detect it first: a manifest has a
  // rootCohort; an ASL interface has asl/States. Same node/edge shape either way, so
  // GraphVisualization is agnostic.
  if (Array.isArray(design.rootCohort)) {
    try {
      const g = manifestToReactFlow(design);
      return (g && (g.nodes?.length || g.edges?.length)) ? g : null;
    } catch {
      return null;
    }
  }
  // design is the full ASL interface: { asl, template_type, agents, ... }
  const asl = design.asl ?? (design.States && design.StartAt ? design : null);
  if (!asl || typeof asl !== 'object') return null;
  try {
    const g = aslToReactFlow(asl);
    return (g && (g.nodes?.length || g.edges?.length)) ? g : null;
  } catch {
    return null;
  }
}

// ─── Utilities ──────────────────────────────────────────────────────────────

function upsertStageList(prev, next) {
  const id = next.stage_id ?? null;
  const name = next.stage_name ?? null;
  const idx = prev.findIndex(
    (s) => (id && s.stage_id === id) || (name && s.stage_name === name),
  );
  if (idx >= 0) {
    const merged = { ...prev[idx], ...next };
    if (!merged.stage_id && id) merged.stage_id = id;
    const updated = prev.slice();
    updated[idx] = merged;
    return updated;
  }
  return [...prev, next];
}

// ─── Initial state ──────────────────────────────────────────────────────────

export const INITIAL_STATE = {
  clientMessages: [],
  eventLog: [],
  _seqCounter: 0,

  stages: [],
  activeStage: null,
  artifact: null,
  isProcessing: false,
  inputState: 'idle',
  approvalState: { pending: false, taskToken: null, awaitingFeedback: false },
  approvalDecisions: {},
  approvalLoadError: false,
  pendingTaskIdForRetry: null,
  currentConnectionId: null,
  buildActivity: EMPTY_BUILD_ACTIVITY,
};

// ─── Build-activity projections (ephemeral) ──────────────────────────────────

function projectBuildPhase(state, payload) {
  const phase = payload && payload.phase ? String(payload.phase) : null;
  if (!phase) return state;
  return { ...state, buildActivity: { ...state.buildActivity, phase } };
}

function projectBuildReasoning(state, payload) {
  const phase = payload && payload.phase ? String(payload.phase) : 'delta';
  const ba = state.buildActivity;
  if (phase === 'done') {
    // Final consolidated reasoning (persisted) — swap the live stream for the stable text.
    const full = typeof payload.fullText === 'string' ? payload.fullText : ba.reasoning;
    return { ...state, buildActivity: { ...ba, reasoning: full, reasoningDone: true } };
  }
  // delta: append the fragment to the live reasoning buffer.
  const frag = typeof payload.text === 'string' ? payload.text : '';
  if (!frag) return state;
  return { ...state, buildActivity: { ...ba, reasoning: (ba.reasoning || '') + frag } };
}

function projectBuildAgentRow(state, payload) {
  const agentId = payload && payload.agentId ? String(payload.agentId) : '';
  const phase = payload && payload.phase ? String(payload.phase) : '';
  if (!agentId || !(phase in AGENT_PHASE_RANK)) return state;
  const agents = state.buildActivity.agents;
  const prev = agents[agentId];
  // Monotonic guard: never regress a row to an earlier phase.
  if (prev && AGENT_PHASE_RANK[phase] < AGENT_PHASE_RANK[prev.phase]) return state;
  const next = {
    ...prev,
    agentId,
    label: payload.label || (prev && prev.label) || agentId,
    phase,
  };
  if (payload.prompt) next.prompt = payload.prompt;
  if (payload.error) next.error = payload.error;
  if (payload.usage) next.usage = payload.usage;  // {modelId, points, estimatedCostUsd} → decks
  return {
    ...state,
    buildActivity: { ...state.buildActivity, agents: { ...agents, [agentId]: next } },
  };
}

function projectBuildSkillRow(state, payload) {
  const name = payload && payload.name ? String(payload.name) : '';
  const phase = payload && payload.phase ? String(payload.phase) : '';
  if (!name || !(phase in SKILL_PHASE_RANK)) return state;
  const skills = state.buildActivity.skills;
  const prev = skills[name];
  if (prev && SKILL_PHASE_RANK[phase] < SKILL_PHASE_RANK[prev.phase]) return state;
  const next = { ...prev, name, phase };
  if (payload.skillId) next.skillId = payload.skillId;
  if (payload.whenToUse) next.whenToUse = payload.whenToUse;
  if (payload.usage) next.usage = payload.usage;  // per-skill cost → its own chip
  if (payload.error) next.error = payload.error;
  return {
    ...state,
    buildActivity: { ...state.buildActivity, skills: { ...skills, [name]: next } },
  };
}

function projectBuildPlaybookRow(state, payload) {
  // Project networks: one row per MILESTONE grooming its task-DAG (queued → planning → done),
  // keyed by milestoneId, exact parity with agent/skill rows (monotonic phase, own cost chip).
  const milestoneId = payload && payload.milestoneId ? String(payload.milestoneId) : '';
  const phase = payload && payload.phase ? String(payload.phase) : '';
  if (!milestoneId || !(phase in PLAYBOOK_PHASE_RANK)) return state;
  const playbook = state.buildActivity.playbook || {};
  const prev = playbook[milestoneId];
  if (prev && PLAYBOOK_PHASE_RANK[phase] < PLAYBOOK_PHASE_RANK[prev.phase]) return state;
  const next = { ...prev, milestoneId, phase };
  next.milestoneName = payload.milestoneName || (prev && prev.milestoneName) || milestoneId;
  if (payload.exitCriterion) next.exitCriterion = payload.exitCriterion;
  if (payload.taskCount != null) next.taskCount = payload.taskCount;
  if (Array.isArray(payload.tasks)) next.tasks = payload.tasks;  // children for the tree view
  if (payload.usage) next.usage = payload.usage;  // per-milestone $/token → its own chip
  if (payload.error) next.error = payload.error;
  return {
    ...state,
    buildActivity: { ...state.buildActivity, playbook: { ...playbook, [milestoneId]: next } },
  };
}

// The frozen plan (a project's playbook / the suggested plan / an expert network's Teams) → one 'done' row per
// milestone with its tasks — the SAME parent→children tree the live build streamed. Frozen tasks key their milestone
// by ID (e.g. "M0", "T0").
function _playbookRows(pb) {
  const out = {};
  if (!pb || typeof pb !== 'object') return out;
  const childrenByMs = {};
  for (const t of (Array.isArray(pb.tasks) ? pb.tasks : [])) {
    if (t && typeof t === 'object' && t.milestone) {
      (childrenByMs[t.milestone] = childrenByMs[t.milestone] || []).push({
        title: t.title || t.id || '', leadAgent: t.leadAgent || '',
      });
    }
  }
  for (const m of (Array.isArray(pb.milestones) ? pb.milestones : [])) {
    if (!m || typeof m !== 'object' || !m.id) continue;
    const kids = childrenByMs[m.id] || [];
    out[m.id] = {
      milestoneId: m.id,
      milestoneName: m.name || m.id,
      phase: 'done',
      taskCount: kids.length,
      tasks: kids,
      ...(m.exitCriterion ? { exitCriterion: m.exitCriterion } : {}),
    };
  }
  return out;
}

// A brain2 (and engine) build fans work out via Step Functions DISTRIBUTED MAP,
// which runs each item as a SEPARATE child execution. EventBridge re-emits every
// child's RUNNING/SUCCEEDED/FAILED, and — because the child input carries the parent's
// networkId/taskId — the SFN→AppSync bridge forwards each as EXECUTION_STARTED /
// EXECUTION_COMPLETED / EXECUTION_FAILED on the SAME build channel. If the reducer
// acted on those, every one of the ~10-20 children finishing would flip isProcessing
// off (spinner flicker), fire the completion signal (→ silent network refetch →
// HYDRATE_BUILD overwrites the live rows with the PARTIAL persisted manifest → "20
// skills ended at 10"), or — on a single failed child — wipe the whole build box.
// The Simple brain is one SFN with no children, so it never had this.
//
// The authoritative discriminator is the execution ARN: a Distributed-Map CHILD
// execution's ARN embeds the MapRun label as ``…:execution:<StateMachine>/<MapRunLabel>:<child>``
// — i.e. a ``/`` inside the segment after ``:execution:``. A top-level run is
// ``…:execution:<StateMachine>:<name>`` (no slash). So a lifecycle event whose
// executionArn has that slash is a child and MUST NOT drive the top-level build
// state. (Backend also skips these at the bridge via ``mapRunArn``; this is the
// belt-and-suspenders that protects a live build even before that deploy lands and
// for the engine's own map children.)
function isDistributedMapChildEvent(event) {
  const payload = (event && (event.payload || event.data)) || {};
  const arn = String(payload.executionArn || event.executionArn || '');
  const marker = ':execution:';
  const at = arn.indexOf(marker);
  if (at < 0) return false;
  // The part after ``:execution:`` is ``<StateMachineName>[/<MapRunLabel>]:<execName>``.
  // A child (map iteration) has a ``/`` in that tail; a top-level run does not.
  const tail = arn.slice(at + marker.length);
  return tail.includes('/');
}

// ─── Server event projections ───────────────────────────────────────────────

function projectStatusUpdate(state, event) {
  const { status, payload, timestamp } = event;

  if (status === 'APPROVAL_REQUIRED') {
    const { taskToken, design } = payload || {};
    if (!taskToken) return state;

    const approvalGraph = graphFromDesign(design);
    const nextArtifact = approvalGraph || state.artifact;

    return {
      ...state,
      inputState: 'approval_required',
      approvalState: { pending: true, taskToken, awaitingFeedback: false },
      isProcessing: false,
      artifact: nextArtifact,
      stages: upsertStageList(state.stages, {
        stage_id: APPROVAL_STAGE_ID,
        stage_name: 'Approval Required',
        status: 'APPROVAL_REQUIRED',
        requiresApproval: true,
        taskToken,
        description: 'Please review and approve the architecture',
        content: [],
        timestamp: timestamp || new Date().toISOString(),
      }),
      activeStage: 'Approval Required',
    };
  }

  if (status === 'INITIATED') {
    return {
      ...state,
      isProcessing: true,
      inputState: 'workflow_running',
    };
  }

  const isFailed = status === 'FAILED'
    || status === 'REJECTED'
    || status === 'DENIED'
    || status === 'TIMED_OUT'
    || status === 'ABORTED'
    || status.includes('ERROR')
    || status.includes('FAIL');

  if (isFailed) {
    return {
      ...state, isProcessing: false, inputState: 'idle', activeStage: null,
      buildActivity: EMPTY_BUILD_ACTIVITY,
    };
  }

  const isTerminal = status === 'SUCCESS';
  if (isTerminal) {
    // KEEP buildActivity: the live build already accumulated the full picture
    // (agents + skills + usage). Clearing it here erased the skills — the box
    // re-hydrates from ``network.latestBuild``, but that manifest isn't in the
    // in-memory network object yet (it was fetched before this build wrote it),
    // so it fell back to the live AGENT rows (no skills). The slice is reset on
    // the next prompt (SEND_MESSAGE) / network switch instead.
    return {
      ...state,
      isProcessing: false,
      inputState: 'idle',
      activeStage: null,
      _completionSignal: true,
    };
  }

  const isComplete = status.endsWith('ED') || status === 'VALIDATED';
  if (isComplete) {
    // Intermediate completed statuses (VISUALIZED, VALIDATED, etc.) mark their
    // stage as done but do NOT stop the overall pipeline or signal completion.
    const displayName = status
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
    const payloadContent = payload
      ? typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2)
      : null;
    const newStages = upsertStageList(state.stages, {
      stage_id: `status:${status}`,
      stage_name: displayName,
      status: 'success',
      description: status,
      content: payloadContent ? [{ content: payloadContent, content_type: 'json' }] : [],
      timestamp: timestamp || new Date().toISOString(),
    });
    return {
      ...state,
      stages: newStages,
      activeStage: null,
    };
  }

  // Any non-failed, non-terminal, non-completed status is treated as in-progress.
  // Backend statuses come in many shapes (GENERATING_PROMPTS, GENERATING_ASL,
  // DEPLOYING, VISUALIZING, etc.) — they all should drive the spinner label.
  const displayName = status
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');

  const payloadContent = payload
    ? typeof payload === 'string'
      ? payload
      : JSON.stringify(payload, null, 2)
    : null;

  const newStages = upsertStageList(state.stages, {
    stage_id: `status:${status}`,
    stage_name: displayName,
    status: 'in_progress',
    description: status,
    content: payloadContent ? [{ content: payloadContent, content_type: 'json' }] : [],
    timestamp: timestamp || new Date().toISOString(),
  });

  return {
    ...state,
    stages: newStages,
    activeStage: displayName,
    isProcessing: true,
    inputState: state.inputState === 'approval_required' ? state.inputState : 'workflow_running',
  };
}

function projectStageTransition(state, data) {
  const { stage_name, status, description, progress_percent } = data;
  const existingIndex = state.stages.findIndex((s) => s.stage_name === stage_name);

  let newStages;
  if (existingIndex >= 0) {
    newStages = state.stages.slice();
    newStages[existingIndex] = { ...newStages[existingIndex], status, description, progress_percent };
  } else {
    newStages = [...state.stages, { stage_name, status, description, progress_percent, content: [] }];
  }

  return {
    ...state,
    stages: newStages,
    activeStage: (status === 'started' || status === 'in_progress') ? stage_name : state.activeStage,
  };
}

function projectStageStream(state, data) {
  const { stage_name, content, content_type } = data;
  const stageIndex = state.stages.findIndex((s) => s.stage_name === stage_name);
  if (stageIndex < 0) return state;

  const newStages = state.stages.slice();
  newStages[stageIndex] = {
    ...newStages[stageIndex],
    content: [...(newStages[stageIndex].content || []), { content, content_type }],
  };
  return { ...state, stages: newStages };
}

function projectStageComplete(state, data) {
  const { stage_name, status } = data;
  const stageIndex = state.stages.findIndex((s) => s.stage_name === stage_name);
  if (stageIndex < 0) return state;

  const newStages = state.stages.slice();
  newStages[stageIndex] = { ...newStages[stageIndex], status: status || 'success' };

  return {
    ...state,
    stages: newStages,
    activeStage: state.activeStage === stage_name ? null : state.activeStage,
  };
}

function projectArtifactEmit(state, data) {
  const { type, payload } = data;
  if (type === 'REACT_FLOW_JSON' && payload) {
    return { ...state, artifact: payload };
  }
  return state;
}

function projectApprovalRequested(state, event) {
  const payload = event.payload || {};
  const { taskToken, design, agentName } = payload;
  if (!taskToken) return state;

  // Build the graph from the design's ASL so it's visible before approval.
  // Keep any existing artifact if the new design yields nothing.
  const approvalGraph = graphFromDesign(design);
  const nextArtifact = approvalGraph || state.artifact;

  return {
    ...state,
    inputState: 'approval_required',
    approvalState: { pending: true, taskToken, awaitingFeedback: false },
    isProcessing: false,
    approvalLoadError: false,
    artifact: nextArtifact,
    stages: upsertStageList(state.stages, {
      stage_id: APPROVAL_STAGE_ID,
      stage_name: 'Approval Required',
      status: 'APPROVAL_REQUIRED',
      requiresApproval: true,
      taskToken,
      description: agentName
        ? `${agentName} is requesting approval`
        : 'Please review and approve the architecture',
      content: [],
      timestamp: event.timestamp || new Date().toISOString(),
    }),
    activeStage: 'Approval Required',
  };
}

function projectUiState(state, payload) {
  const uiState = (payload.uiState || '').toString().toUpperCase();
  switch (uiState) {
    case 'AWAITING_APPROVAL':
      return {
        ...state,
        inputState: 'approval_required',
        isProcessing: false,
        approvalState: payload.taskToken
          ? { ...state.approvalState, pending: true, taskToken: payload.taskToken }
          : state.approvalState,
      };
    case 'RUNNING':
      return { ...state, isProcessing: true, inputState: 'workflow_running' };
    case 'IDLE':
      return { ...state, isProcessing: false, inputState: 'idle', activeStage: null };
    case 'AWAITING_INITIAL_INPUT':
      return { ...state, inputState: 'idle' };
    default:
      return state;
  }
}

function projectExecutionFailed(state, event) {
  const payload = event.payload || {};
  const errText = payload.error || payload.cause || payload.status || 'Execution failed';

  let newStages = state.stages;
  if (newStages.length > 0) {
    const updated = [...newStages];
    for (let i = updated.length - 1; i >= 0; i -= 1) {
      const s = updated[i];
      if (s.status === 'in_progress' || s.status === 'started' || s.status === 'APPROVAL_REQUIRED') {
        updated[i] = { ...s, status: 'failed', description: errText };
        newStages = updated;
        break;
      }
    }
  }

  return {
    ...state,
    isProcessing: false,
    inputState: 'idle',
    activeStage: null,
    approvalState: { pending: false, taskToken: null, awaitingFeedback: false },
    stages: newStages,
    buildActivity: EMPTY_BUILD_ACTIVITY,
  };
}

function projectServerEvent(state, event) {
  const messageType = event.type || event.eventType;
  const messageTypeNorm = (messageType || '').toString().toLowerCase();

  if (messageTypeNorm === 'status_update') {
    return projectStatusUpdate(state, event);
  }

  const eventType = event.eventType;
  const payload = event.payload || event.data || {};

  switch (eventType) {
    case 'PROCESS_START':
      return { ...state, isProcessing: true };

    case 'STAGE_TRANSITION':
      return projectStageTransition(state, payload);

    case 'STAGE_STREAM':
      return projectStageStream(state, payload);

    case 'STAGE_COMPLETE':
      return projectStageComplete(state, payload);

    case 'ARTIFACT_EMIT':
      return projectArtifactEmit(state, payload);

    case 'APPROVAL_REQUESTED':
      return projectApprovalRequested(state, event);

    case 'USER_PROMPT': {
      const promptText = payload.prompt || '';
      if (!promptText) return state;
      // Whitespace-normalized match so a live prompt doesn't double a hydrated
      // one that differs only cosmetically (matches HYDRATE's dedup).
      const norm = (s) => String(s).replace(/\s+/g, ' ').trim();
      const normPrompt = norm(promptText);
      const alreadyHas = state.clientMessages.some(
        (m) => m.role === 'user' && norm(m.content) === normPrompt,
      );
      if (alreadyHas) return state;
      return {
        ...state,
        clientMessages: [...state.clientMessages, {
          id: event.eventId || event.correlationId || crypto.randomUUID(),
          role: 'user',
          content: promptText,
          timestamp: event.timestamp || new Date().toISOString(),
          actorSub: payload.actorSub || null,
        }],
      };
    }

    case 'UI_STATE':
      return projectUiState(state, payload);

    // Ephemeral build-activity (Brain network generation). These drive the
    // activity panel and are never persisted to clientMessages.
    case 'BUILD_PHASE':
      return projectBuildPhase(state, payload);
    case 'BUILD_REASONING':
      return projectBuildReasoning(state, payload);
    case 'BUILD_AGENT_ROW':
      return projectBuildAgentRow(state, payload);
    case 'BUILD_SKILL_ROW':
      return projectBuildSkillRow(state, payload);
    case 'BUILD_PLAYBOOK_ROW':
      return projectBuildPlaybookRow(state, payload);

    case 'COMPLETE':
    case 'EXECUTION_COMPLETED':
      // A Distributed-Map CHILD finishing is NOT the build finishing — ignore it,
      // or it drops the spinner + fires the completion signal (→ refetch →
      // HYDRATE_BUILD overwrites the live rows with the partial manifest). Only the
      // parent brain2 execution's terminal is the real "build done".
      if (isDistributedMapChildEvent(event)) return state;
      // KEEP buildActivity on success (see the SUCCESS branch above) — the
      // accumulated agents + skills + usage stay visible; reset on next prompt.
      return {
        ...state,
        isProcessing: false,
        inputState: 'idle',
        activeStage: null,
        _completionSignal: true,
      };

    case 'ERROR':
      if (isDistributedMapChildEvent(event)) return state;
      return {
        ...state, isProcessing: false, inputState: 'idle', activeStage: null,
        buildActivity: EMPTY_BUILD_ACTIVITY,
      };

    case 'EXECUTION_STARTED':
      // A child execution STARTING must not re-assert build state either (harmless
      // but keeps the stream clean + symmetric with the terminal guards).
      if (isDistributedMapChildEvent(event)) return state;
      return {
        ...state,
        isProcessing: true,
        inputState: state.inputState !== 'approval_required' ? 'workflow_running' : state.inputState,
      };

    case 'EXECUTION_FAILED':
      // A single failed skill/agent CHILD (best-effort; the build proceeds) must
      // never wipe the whole build box — only a failure of the top-level execution.
      if (isDistributedMapChildEvent(event)) return state;
      return projectExecutionFailed(state, event);

    case 'APPROVAL_RESOLVED': {
      // Cross-window fan-out: another window resolved this approval. Clear the
      // pending-approval tag HERE too, so the window that only WATCHED (e.g.
      // prompted here, approved elsewhere) doesn't stay stuck on "awaiting
      // approval". The backend publishes taskToken to match what we stored in
      // approvalState (projectApprovalRequested). Guard on taskToken so a stale
      // resolution can't dismiss a newer pending approval; if we aren't pending
      // (this window did the approving — already cleared locally), no-op.
      const resolvedToken = payload.taskToken || event.correlationId || '';
      if (
        state.approvalState?.pending
        && (!resolvedToken || resolvedToken === state.approvalState.taskToken)
      ) {
        return {
          ...state,
          isProcessing: false,
          inputState: 'idle',
          activeStage: null,
          approvalState: { pending: false, taskToken: null, awaitingFeedback: false },
        };
      }
      return state;
    }

    case 'SFN_TRANSITION':
    case 'STATUS_UPDATE':
    case 'AGENT_MESSAGE':
    case 'AGENT_FINAL_RESPONSE':
    case 'APPROVAL_CLAIMED':
    case 'APPROVAL_DELEGATED':
    case 'APPROVAL_ESCALATED':
    case 'APPROVAL_CLAIM_EXPIRED':
    case 'MEMBERSHIP_GRANTED':
    case 'MEMBERSHIP_REVOKED':
    case 'MEMBERSHIP_SELF_ELEVATED':
    case 'WORKBENCH_PRESENCE_JOINED':
    case 'WORKBENCH_PRESENCE_LEFT':
    case 'WORKBENCH_TYPING':
    case 'FIELD_LOCK_ACQUIRED':
    case 'FIELD_LOCK_RELEASED':
    case 'NETWORK_RELEASED':
      return state;

    default:
      return state;
  }
}

// ─── Reducer ────────────────────────────────────────────────────────────────

export function brainSessionReducer(state, action) {
  switch (action.type) {
    case 'client/NETWORK_SWITCH':
      return { ...INITIAL_STATE };

    case 'client/SEND_MESSAGE': {
      const msg = {
        id: action.payload.id,
        role: action.payload.role || 'user',
        content: action.payload.content,
        timestamp: action.payload.timestamp || new Date().toISOString(),
      };
      const isError = action.payload.role === 'assistant';
      if (isError) {
        return {
          ...state,
          clientMessages: action.payload.replace ? [msg] : [...state.clientMessages, msg],
          inputState: 'idle',
          isProcessing: false,
          activeStage: null,
          stages: [],
          buildActivity: EMPTY_BUILD_ACTIVITY,
        };
      }
      // When the user submits feedback during a modify cycle the diagram
      // they were reviewing must come down — the next architect pass will
      // produce a fresh one. Otherwise (normal prompt during a build) we
      // leave the artifact alone; ``client/MODIFY`` keeps it visible
      // intentionally so the user can read it while composing feedback.
      const wasAwaitingFeedback = state.approvalState?.awaitingFeedback === true;
      return {
        ...state,
        clientMessages: action.payload.replace ? [msg] : [...state.clientMessages, msg],
        inputState: 'workflow_running',
        isProcessing: true,
        activeStage: 'Processing',
        stages: [{ stage_id: 'processing', stage_name: 'Processing', status: 'in_progress', description: 'Processing', content: [], timestamp: new Date().toISOString() }],
        buildActivity: EMPTY_BUILD_ACTIVITY,
        ...(wasAwaitingFeedback ? { artifact: null, approvalState: { pending: false, taskToken: null, awaitingFeedback: false } } : {}),
      };
    }

    case 'client/HYDRATE_CHAT_HISTORY': {
      // Dedup by (role, content) against what's already present AND within the
      // incoming history. The old ``length > 0`` all-or-nothing guard caused
      // duplicate user prompts on REVISIT: the hydration effect re-fires when
      // ``network.chatHistory`` gets a fresh reference (silent refetch), and a
      // USER_PROMPT also arrives via the AppSync channel replay — same content,
      // different ids, so neither path's guard caught the other. Merging by
      // content makes hydration idempotent and collision-proof regardless of
      // order. (USER_PROMPT's own content dedup at the case above is the mirror.)
      // Normalize whitespace in the dedup key — persisted history can hold the
      // same prompt twice with a cosmetic difference (e.g. a leading non-breaking
      // space from the composer), which an exact-match key would miss.
      const dedupKey = (role, content) => `${role} ${String(content).replace(/\s+/g, ' ').trim()}`;
      const seen = new Set(
        state.clientMessages.map((m) => dedupKey(m.role, m.content)),
      );
      const merged = state.clientMessages.slice();
      for (const m of action.payload.history) {
        const role = (m && m.role) || 'user';
        const content = typeof m?.content === 'string' ? m.content : String(m?.content ?? '');
        const key = dedupKey(role, content);
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push({
          id: crypto.randomUUID(),
          role,
          content,
          timestamp: (m && m.timestamp) || new Date().toISOString(),
        });
      }
      return { ...state, clientMessages: merged };
    }

    case 'client/HYDRATE_GRAPH': {
      const g = action.payload.graph;
      // ``latestGraph`` from ``GET /networks/{id}`` is the authoritative ASL
      // after every successful Brain run. The previous guard kept whatever
      // was already in ``state.artifact`` (so a stale approval-preview
      // diagram could stick after an update completed). On hydration with
      // a real graph, always trust the server payload; only keep the
      // existing artifact when the incoming graph is empty.
      if (!g || (!g.nodes?.length && !g.edges?.length)) return state;
      return { ...state, artifact: g };
    }

    case 'client/HYDRATE_LIVE_BUILD': {
      // THE BUILD SNAPSHOT (L175): the server's per-row snapshot of the CURRENT build (GET /networks/{id} →
      // ``liveBuild``) replayed through the SAME projections the live events use — so leaving the page (an Inbox
      // click) and coming back, or a reload mid-build, shows exactly what streamed. Same build → merged (the
      // monotonic guards keep any newer live row); a different build → starts clean.
      const live = (action.payload && action.payload.live) || null;
      if (!live || !live.taskId) return state;
      const same = state.buildActivity && state.buildActivity.taskId === live.taskId;
      let st = { ...state, buildActivity: same ? state.buildActivity : { ...EMPTY_BUILD_ACTIVITY, taskId: live.taskId } };
      const ended = ['DONE', 'FAILED'].includes(String(live.phase || '').toUpperCase());
      if (live.phase && !ended) st = projectBuildPhase(st, { phase: live.phase, status: live.phaseStatus });
      for (const row of live.agents || []) st = projectBuildAgentRow(st, row);
      for (const row of live.skills || []) st = projectBuildSkillRow(st, row);
      for (const row of live.playbook || []) st = projectBuildPlaybookRow(st, row);
      st = { ...st, buildActivity: { ...st.buildActivity, taskId: live.taskId } };
      // still running → the spinner + the completion refetch (true→false) work after a remount too
      return live.running ? { ...st, isProcessing: true } : st;
    }

    case 'client/HYDRATE_BUILD': {
      // Rebuild the build box on revisit so prompts + skills + per-agent
      // consumption survive a reload — matching how the room replays its feed.
      //
      // TWO sources, in priority order:
      //  1. ``build`` — the IMMUTABLE per-revision manifest (builds/{taskId}.json).
      //     This is what the LLM GENERATED: agent systemPrompts, skills, usage.
      //     Preferred because it is frozen against later user edits (editing an
      //     agent's prompt in the Agents tab overwrites the live AGENT# row, but
      //     never this manifest). Carries skills, which the AGENT rows don't.
      //  2. ``agents`` — the live AGENT# row map from GET /networks/{id}. Legacy
      //     fallback for networks built before manifests existed; shows whatever
      //     the current (possibly edited) systemPrompt is, and no skills.
      // Don't disturb a live build in progress.
      if (state.isProcessing) return state;
      const p = action.payload || {};

      // 1) Manifest path — generated artifacts. Only taken when the manifest
      // actually carries agents (else fall through to the live-rows fallback).
      const build = p.build;
      if (build && typeof build === 'object' && Array.isArray(build.agents) && build.agents.length > 0) {
        const rows = {};
        for (const a of build.agents) {
          if (!a || typeof a !== 'object') continue;
          const id = a.logicalId;
          if (!id || !a.systemPrompt) continue;
          rows[id] = {
            agentId: id,
            label: id,
            phase: 'done',
            prompt: a.systemPrompt,
            ...(a.usage && typeof a.usage === 'object' ? { usage: a.usage } : {}),
          };
        }
        const skillRows = {};
        for (const s of (Array.isArray(build.skills) ? build.skills : [])) {
          if (!s || typeof s !== 'object' || !s.name) continue;
          skillRows[s.name] = {
            name: s.name,
            phase: 'done',
            ...(s.skillId ? { skillId: s.skillId } : {}),
            ...(s.whenToUse ? { whenToUse: s.whenToUse } : {}),
            ...(s.instructions ? { instructions: s.instructions } : {}),
            ...(s.usage && typeof s.usage === 'object' ? { usage: s.usage } : {}),
          };
        }
        // Playbook (project networks): rebuild one 'done' row per milestone from the frozen
        // manifest playbook, with its task count — so revisiting a built project shows the same
        // milestone rows the live build streamed. Absent on transactional networks.
        const playbookRows = {};
        // The frozen playbook is a SEPARATE network field (``p.playbook``) — it is NOT inside the
        // per-build manifest (``build.playbook`` is normally absent). Prefer the network-level one so
        // the milestone tree rehydrates on revisit like agents/skills; keep build.playbook as a fallback.
        const _pb = (p.playbook && typeof p.playbook === 'object') ? p.playbook
          : (build.playbook && typeof build.playbook === 'object' ? build.playbook : null);
        Object.assign(playbookRows, _playbookRows(_pb));
        if (Object.keys(rows).length === 0 && Object.keys(skillRows).length === 0
            && Object.keys(playbookRows).length === 0) return state;
        return {
          ...state,
          buildActivity: {
            phase: null,
            agents: rows,
            skills: skillRows,
            playbook: playbookRows,
            prompt: typeof build.prompt === 'string' ? build.prompt : null,
            taskId: build.taskId || null,
            architectUsage: (build.architectUsage && typeof build.architectUsage === 'object')
              ? build.architectUsage : null,
            // Architect reasoning is frozen into the per-revision manifest (prompt_engineer folds
            // architect_reasoning.json → ``architectReasoning``). Copy it back so the reasoning
            // block survives a reload — it's the SAME text the live BUILD_REASONING stream showed,
            // and users rely on it to understand WHY the network is shaped the way it is.
            ...(typeof build.architectReasoning === 'string' && build.architectReasoning.length > 0
              ? { reasoning: build.architectReasoning, reasoningDone: true }
              : {}),
          },
        };
      }

      // 2) Legacy fallback — live AGENT rows (no manifest for this network).
      const src = p.agents;
      if (!src || typeof src !== 'object') return state;
      const rows = {};
      for (const [logicalId, cfg] of Object.entries(src)) {
        if (!logicalId || logicalId.startsWith('__') || !cfg || typeof cfg !== 'object') continue;
        const prompt = typeof cfg.systemPrompt === 'string' ? cfg.systemPrompt : undefined;
        const usage = cfg.buildUsage && typeof cfg.buildUsage === 'object' ? cfg.buildUsage : undefined;
        if (!prompt && !usage) continue;
        rows[logicalId] = {
          agentId: logicalId,
          label: cfg.logicalId || logicalId,
          phase: 'done',
          ...(prompt ? { prompt } : {}),
          ...(usage ? { usage } : {}),
        };
      }
      // …and the plan (an expert network deployed from the registry has no build manifest, but its Teams plan is
      // read from the live rulebook — L235)
      const playbookRows = _playbookRows(p.playbook);
      if (Object.keys(rows).length === 0 && Object.keys(playbookRows).length === 0) return state;
      return {
        ...state,
        buildActivity: { phase: null, agents: rows, skills: {}, playbook: playbookRows },
      };
    }

    case 'client/APPROVE':
      return {
        ...state,
        stages: [],
        artifact: null,
        activeStage: null,
        approvalState: { pending: false, taskToken: null, awaitingFeedback: false },
        inputState: 'workflow_running',
        isProcessing: true,
      };

    case 'client/DENY':
      return {
        ...state,
        stages: [],
        artifact: null,
        activeStage: null,
        approvalState: { pending: false, taskToken: null, awaitingFeedback: false },
        inputState: 'idle',
        isProcessing: false,
      };

    case 'client/MODIFY':
      return {
        ...state,
        stages: [],
        // Keep the diagram visible while the user composes feedback.
        // It's wiped on the next ``client/SEND_MESSAGE`` (when feedback
        // is submitted) or replaced when the next APPROVAL_REQUESTED
        // arrives with the revised design.
        activeStage: null,
        // Keep taskToken so BuildTab can send feedback to the paused SFN task.
        // Clear pending so the approval bar hides, but preserve the token.
        approvalState: { pending: false, taskToken: action.payload.taskToken, awaitingFeedback: true },
        inputState: 'modification_mode',
        isProcessing: false,
      };

    case 'client/RECORD_APPROVAL_DECISION':
      return {
        ...state,
        approvalDecisions: {
          ...state.approvalDecisions,
          [action.payload.taskToken]: {
            decision: action.payload.decision,
            timestamp: new Date().toISOString(),
          },
        },
      };

    case 'client/CLEAR_APPROVAL':
      return {
        ...state,
        approvalState: { pending: false, taskToken: null, awaitingFeedback: false },
        stages: action.payload?.clearStages ? [] : state.stages,
      };

    case 'client/RECONCILE_DEPLOYED': {
      // Drop a *stale* unresolved approval (e.g. opening an already-deployed
      // network from a fresh tab — channel-events replay would otherwise show
      // an Approve/Deny panel that's already been resolved). But preserve any
      // approval stage that the user has already decided on — its diagram is
      // part of the build history.
      const hasUndecidedApproval = state.stages.some((st) => {
        if (st.stage_id !== APPROVAL_STAGE_ID) return false;
        const token = st.taskToken;
        return !token || !state.approvalDecisions[token];
      });
      return {
        ...state,
        approvalState: { pending: false, taskToken: null, awaitingFeedback: false },
        pendingTaskIdForRetry: null,
        approvalLoadError: false,
        stages: hasUndecidedApproval
          ? state.stages.filter((st) => st.stage_id !== APPROVAL_STAGE_ID)
          : state.stages,
      };
    }

    case 'client/RESTORE_APPROVAL': {
      const data = action.payload;
      if (!data || data.status !== 'AWAITING_APPROVAL' || !data.taskToken) return state;

      // The restore endpoint returns ``design`` as a JSON STRING (the interface
      // is json.dumps'd server-side), whereas the live APPROVAL_REQUESTED event
      // carries it as an object. graphFromDesign needs an object, so parse here —
      // otherwise the approval chip restores but the diagram is missing (the
      // exact reload regression this guards).
      let design = data.design || data.architectExplanation || null;
      if (typeof design === 'string') {
        try { design = JSON.parse(design); } catch { design = null; }
      }
      const approvalGraph = graphFromDesign(design);
      const nextArtifact = approvalGraph || state.artifact;

      let newMessages = state.clientMessages;
      if (data.prompt && state.clientMessages.length === 0) {
        newMessages = [{
          id: crypto.randomUUID(),
          role: 'user',
          content: data.prompt,
          timestamp: new Date().toISOString(),
        }];
      }

      return {
        ...state,
        clientMessages: newMessages,
        artifact: nextArtifact,
        approvalState: { pending: true, taskToken: data.taskToken, awaitingFeedback: false },
        inputState: 'approval_required',
        stages: upsertStageList(state.stages, {
          stage_id: APPROVAL_STAGE_ID,
          stage_name: 'Approval Required',
          status: 'APPROVAL_REQUIRED',
          requiresApproval: true,
          taskToken: data.taskToken,
          description: 'Please review and approve the architecture',
          content: [],
          timestamp: new Date().toISOString(),
        }),
        activeStage: 'Approval Required',
      };
    }

    case 'client/SET_CONNECTION_ID':
      return { ...state, currentConnectionId: action.payload.connectionId };

    case 'client/SET_APPROVAL_LOAD_ERROR':
      return { ...state, approvalLoadError: action.payload.error };

    case 'client/SET_PENDING_TASK_FOR_RETRY':
      return { ...state, pendingTaskIdForRetry: action.payload.taskId };

    // ─── Server events ────────────────────────────────────────────────

    case 'server/EVENT_RECEIVED': {
      const event = action.payload.event;
      const seq = state._seqCounter + 1;

      if (event.eventId && state.eventLog.some((e) => e.eventId === event.eventId)) {
        return state;
      }

      const logEntry = { ...event, _seq: seq };
      const projected = projectServerEvent(state, event);

      // Keep event log bounded (retain last 200 entries for idempotency checks)
      const newLog = [...state.eventLog, logEntry];
      const trimmedLog = newLog.length > 200 ? newLog.slice(-200) : newLog;

      return {
        ...projected,
        eventLog: trimmedLog,
        _seqCounter: seq,
      };
    }

    default:
      return state;
  }
}

export { upsertStageList, APPROVAL_STAGE_ID };
